import { type ButtonHTMLAttributes, type ReactNode } from "react";
import { cn } from "@/lib/utils";

/**
 * Pill chip (visual-restyle plan §1 "Pill chip" + §6.1 suggestion chips).
 *
 * A `rounded-full`, hairline-bordered, **no-fill** button: `body-sm`,
 * optional leading icon, hover wash, and the canonical gold focus ring.
 * Active = the §6.2 picker recipe (slim gold ring-inset + soft warm
 * wash + warm ink) — used by the Settings theme/language pickers and
 * tag-style filters; the Home suggestion chips leave `active` unset.
 *
 * Lexiun-local for now (plan §10 defers shared cross-SPA primitives to
 * the org-admin reskin).
 */
export interface ChipProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  /** Selected/active state — gold ring-inset border + warm wash. */
  active?: boolean;
  /** Leading icon node (caller sizes it, e.g. `<Search size={13} />`). */
  leading?: ReactNode;
}

export function Chip({ active, leading, children, className, type, ...rest }: ChipProps) {
  return (
    <button
      type={type ?? "button"}
      data-active={active || undefined}
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-body-sm transition-colors",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold-500/40 focus-visible:ring-offset-2 focus-visible:ring-offset-cream-50 dark:focus-visible:ring-offset-coffee-900",
        active
          ? "border-gold-500/60 bg-cream-100/60 text-coffee-900 dark:border-gold-500/50 dark:bg-coffee-800/60 dark:text-cream-100"
          : "border-cream-200/60 text-coffee-600 hover:bg-cream-100/60 hover:text-coffee-900 dark:border-coffee-700/60 dark:text-cream-400 dark:hover:bg-coffee-800/60 dark:hover:text-cream-100",
        className,
      )}
      {...rest}
    >
      {leading}
      <span className="leading-tight">{children}</span>
    </button>
  );
}
